;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { setLocalStorageItem } from "@/lib/browser-storage/localStorage";
import {
	type CreateDataProviderSchema,
	createDataProviderSchema,
} from "@/lib/schema/data-provider";
import { getQueryClient } from "@/queries/config/query-client";
import { queryFetcher } from "@/queries/config/query-fetcher";
import { integrationQueryKeys } from "@/queries/query-keys/integrations";
import { ProviderType } from "@/types/dataProvider";
import { INTEGRATIONS_LOCAL_STORAGE_KEYS, PROVIDER_NAMES } from "../constants";
import {
	createSimpleProviderConfig,
	type ProviderConfig,
} from "./provider-config";
import { toRentvineVendorOptions } from "./rentvine-vendor-options";

export const unknownConfig: ProviderConfig = createSimpleProviderConfig(
	ProviderType.YARDI,
	["data.credentials.apiKey"],
);

// Placeholders sized to satisfy `createDataProviderSchema` minimums for the
// AppFolio variants so that earlier steps in the wizard can pass full-schema
// validation while later-step credential fields are still hidden. Each
// placeholder is cleared in `onStepSubmit` before the user sees its field.
// clientId requires min length 25; clientSecret requires min length 32.
const APPFOLIO_PLACEHOLDER_CLIENT_ID = "placeholder_client_id_25c";
const APPFOLIO_PLACEHOLDER_CLIENT_SECRET = "placeholder_client_secret_32char";

// Yardi - Simple single-step provider
export const yardiConfig: ProviderConfig = createSimpleProviderConfig(
	ProviderType.YARDI,
	["data.credentials.apiKey"],
);

// Opiniion - Simple single-step provider
export const opiniionConfig: ProviderConfig = createSimpleProviderConfig(
	ProviderType.OPINIION,
	["data.credentials.email", "data.credentials.password"],
);

export const gadminConfig: ProviderConfig = {
	canSubmitStep: () => {
		return true;
	},

	getSubmitButtonText: (stepIndex, totalSteps, isSubmitting) => {
		if (isSubmitting) return "Saving...";
		return stepIndex < totalSteps - 1 ? "Next" : "Save";
	},

	onFinalSubmit: async (data, { createProvider, pingProvider, form }) => {
		try {
			const pingResult = await pingProvider(data);

			if (!pingResult?.isCredentialsValid) {
				form.setError("data.credentials.superAdminEmail", {
					message:
						"The connection could not be established using the provided email address. Please check the email address and try again.",
				});
				return { success: false };
			}

			await createProvider(data);
			return { success: true };
		} catch (_error) {
			form.setError("data.credentials.superAdminEmail", {
				message:
					"An error occurred while validating the credentials. Please try again.",
			});
			return { success: false };
		}
	},
	onInit: (form) => {
		// Set a placeholder value to pass validation on step 0
		form.setValue("data.credentials.superAdminEmail", "placeholder@temp.com");
	},
	onReset: (form) => {
		form.setValue("data.credentials.superAdminEmail", "placeholder@temp.com");
	},
	onStepSubmit: async (stepIndex, _data, { form }) => {
		// Step 0: Instructions page - clear placeholder and advance
		if (stepIndex === 0) {
			// Clear the placeholder so user sees empty field on step 1
			form.setValue("data.credentials.superAdminEmail", "");
			return {
				shouldAdvance: true,
				success: true,
			};
		}

		// Step 1: Validate the actual email
		if (stepIndex === 1) {
			const providerType = form.watch("data.type");
			const email = form.watch("data.credentials.superAdminEmail");

			const parsedEmail = createDataProviderSchema.shape.data.safeParse({
				credentials: {
					superAdminEmail: email,
				},
				type: providerType,
			});

			if (parsedEmail.error) {
				form.setError("data.credentials.superAdminEmail", {
					message: parsedEmail.error.flatten().formErrors.join(", "),
				});

				return {
					shouldAdvance: false,
					success: false,
				};
			}
		}

		// Other steps - advance by default
		return {
			shouldAdvance: true,
			success: true,
		};
	},
	steps: [
		{
			description: "Configure Propstrata API access",
			id: 1,
			title: "Google Admin Portal",
		},
		{
			description: `Connect ${PROVIDER_NAMES[ProviderType.GADMIN]}`,
			id: 2,
			title: "Connection Credentials",
		},
	],
	type: ProviderType.GADMIN,
};
// Gmelius - Simple single-step provider
export const gmeliusConfig: ProviderConfig = createSimpleProviderConfig(
	ProviderType.GMELIUS,
	["data.credentials.clientId", "data.credentials.clientSecret"],
);

// AppFolio Reporting API - Complex multi-step provider
export const appFolioReportingApiConfig: ProviderConfig = {
	canSubmitStep: () => {
		return true;
	},

	getSubmitButtonText: (stepIndex, totalSteps, isSubmitting) => {
		if (isSubmitting) return "Saving...";
		return stepIndex < totalSteps - 1 ? "Next" : "Save";
	},

	onFinalSubmit: async (data, { createProvider, form }) => {
		try {
			const watchValues = form.watch();
			const validatedResult = createDataProviderSchema.safeParse(watchValues);

			if (!validatedResult.success) {
				for (const error of validatedResult.error.issues) {
					form.setError(error.path[0] as any, {
						message: error.message,
					});
				}
				return { success: false };
			}

			await createProvider(data);

			return { success: true };
		} catch (_error) {
			return { success: false };
		}
	},

	onInit: (form) => {
		// Pre-fill later-step fields with schema-valid placeholders so the
		// Database Name step can submit through the full-schema resolver.
		// `onStepSubmit` clears these before the user sees their fields.
		form.setValue("data.propertyGroupIds", [1]);
		form.setValue("data.credentials.clientId", APPFOLIO_PLACEHOLDER_CLIENT_ID);
		form.setValue(
			"data.credentials.clientSecret",
			APPFOLIO_PLACEHOLDER_CLIENT_SECRET,
		);
	},

	onReset: (form) => {
		form.setValue("data.propertyGroupIds", []);
	},

	onStepSubmit: async (stepIndex, data, { pingProvider, form }) => {
		// Step 0: Validate database name
		if (stepIndex === 0) {
			const databaseName = form.watch("data.credentials.databaseName") ?? "";
			if (databaseName.length === 0) {
				form.setError("data.credentials.databaseName", {
					message: "Database name is required",
				});

				return {
					shouldAdvance: false,
					success: false,
				};
			}

			form.setValue("data.credentials.clientId", "");
			form.setValue("data.credentials.clientSecret", "");
		}

		// Step 1: Validate credentials and fetch property groups
		if (stepIndex === 1) {
			const pingResult = await pingProvider(data);

			if (!pingResult?.isCredentialsValid) {
				const errorFields = [
					"data.credentials.clientId",
					"data.credentials.clientSecret",
					"data.credentials.databaseName",
				];

				for (const errorKey of errorFields) {
					form.setError(errorKey as any, {
						message: "This provided credential is possibly incorrect",
					});
				}

				return {
					errorFields,
					shouldAdvance: false,
					success: false,
				};
			}

			// Extract and format property groups
			const groups: { id: string | number; name: string }[] = Array.isArray(
				pingResult?.propertyGroups,
			)
				? (pingResult.propertyGroups as any[]).map((g: any) => ({
						id: String(
							g.groupId ??
								g.id ??
								g.property_group_id ??
								g.portfolio_id ??
								g.property_id,
						),
						name: String(
							g.groupName ??
								g.name ??
								g.property_group_name ??
								g.portfolio ??
								g.property_name ??
								g.property,
						),
					}))
				: Array.isArray(pingResult?.properties)
					? (pingResult.properties as any[]).map((p: any) => ({
							id: String(
								p.property_group_id ?? p.portfolio_id ?? p.property_id ?? p.id,
							),
							name: String(
								p.property_group_name ??
									p.portfolio ??
									p.property_name ??
									p.property,
							),
						}))
					: [];

			// Deduplicate and format property groups
			const dedup = new Map<string, string>();
			for (const g of groups) {
				if (!g.id || !g.name) continue;
				dedup.set(String(g.id), String(g.name));
			}

			const propertyGroupOptions = Array.from(dedup.entries())
				.map(([id, name]) => ({
					label: name,
					value: id,
				}))
				.sort((a, b) => {
					const aNum = Number(a.value);
					const bNum = Number(b.value);
					if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
					return String(a.value).localeCompare(String(b.value));
				});

			// Default to all fetched groups so users can deselect instead of selecting manually.
			form.setValue(
				"data.propertyGroupIds",
				propertyGroupOptions.map((option) => option.value),
			);

			return {
				data: { propertyGroupOptions },
				shouldAdvance: true,
				success: true,
			};
		}

		return { shouldAdvance: true, success: true };
	},
	steps: [
		{
			description: "Provide database name",
			id: 1,
			title: "Database Name",
		},
		{
			description: `Connect ${PROVIDER_NAMES[ProviderType.APP_FOOLIO]}`,
			id: 2,
			title: "Connection Secrets",
		},
		{
			description: "Configure property groups",
			id: 3,
			title: "Property Groups",
		},
	],
	type: ProviderType.APP_FOOLIO,
};

// AppFolio Database API - Complex multi-step provider
export const appFolioDatabaseApiConfig: ProviderConfig = {
	getSubmitButtonText: (stepIndex, totalSteps, isSubmitting) => {
		if (isSubmitting) return "Saving...";
		return stepIndex < totalSteps - 1 ? "Next" : "Verify & Save";
	},
	onFinalSubmit: async (data, { createProvider, pingProvider, form }) => {
		try {
			const pingResult = await pingProvider(data);
			if (!pingResult?.isCredentialsValid) {
				const errorFields = [
					"data.credentials.clientId",
					"data.credentials.clientSecret",
					"data.credentials.developerId",
				] as const;

				for (const errorField of errorFields) {
					form.setError(errorField, {
						message: "This provided credential is possibly incorrect",
					});
				}
				return { errorFields: [...errorFields], success: false };
			}

			await createProvider(data);
			return { success: true };
		} catch (_error) {
			const errorFields = [
				"data.credentials.clientId",
				"data.credentials.clientSecret",
				"data.credentials.developerId",
			] as const;

			for (const errorField of errorFields) {
				form.setError(errorField, {
					message: "This provided credential is possibly incorrect",
				});
			}

			return { errorFields: [...errorFields], success: false };
		}
	},
	onInit: (form) => {
		// Placeholders sized to satisfy `createDataProviderSchema` so the
		// initial Data Provider step can submit before credentials are
		// collected on later steps. `onStepSubmit` clears them in turn.
		form.setValue("data.credentials.clientId", APPFOLIO_PLACEHOLDER_CLIENT_ID);
		form.setValue(
			"data.credentials.clientSecret",
			APPFOLIO_PLACEHOLDER_CLIENT_SECRET,
		);
		form.setValue("data.credentials.developerId", "placeholder");
	},
	onStepSubmit: async (stepIndex, _data, { form }) => {
		if (stepIndex === 0) {
			form.setValue("data.credentials.clientId", "");
			form.setValue("data.credentials.clientSecret", "");
			return { shouldAdvance: true, success: true };
		}

		if (stepIndex !== 1) {
			return { shouldAdvance: true, success: true };
		}

		const clientId = form.watch("data.credentials.clientId") ?? "";
		const clientSecret = form.watch("data.credentials.clientSecret") ?? "";
		let hasValidationError = false;

		if (clientId.length < 25) {
			form.setError("data.credentials.clientId", {
				message: "Client ID is required",
			});
			hasValidationError = true;
		}

		if (clientSecret.length < 32) {
			form.setError("data.credentials.clientSecret", {
				message: "Client Secret is required",
			});
			hasValidationError = true;
		}

		if (hasValidationError) {
			return {
				shouldAdvance: false,
				success: false,
			};
		}

		// Clear placeholder so the user must provide the real value on step 2.
		form.setValue("data.credentials.developerId", "");

		return {
			shouldAdvance: true,
			success: true,
		};
	},
	steps: [
		{
			description: "Choose your data provider",
			id: 1,
			title: "Data Provider",
		},
		{
			description: "Enter your Client ID and Client Secret",
			id: 2,
			title: "Client Credentials",
		},
		{
			description: "Enter your Developer ID to verify the connection",
			id: 3,
			title: "Developer Details",
		},
	],
	type: ProviderType.APPFOLIO_DATABASE,
};

// QuickBooks - Single-step provider
export const quickbooksConfig: ProviderConfig = {
	getSubmitButtonText(_stepIndex, _totalSteps, isSubmitting) {
		// if (stepIndex === 0) return "Connect QuickBooks";
		if (isSubmitting) return "Redirecting...";

		return "Connect QuickBooks";
	},
	onFinalSubmit: async (_data, { form, workspaceId }) => {
		try {
			if (!workspaceId) {
				form.setError("data.type", {
					message: "An unexpected error occurred, try again.",
				});
				return { success: false };
			}

			const queryClient = getQueryClient();
			const result = await queryClient.fetchQuery({
				gcTime: 0,
				queryFn: () =>
					queryFetcher<{ authUrl: string }>(
						`/api/workspaces/${workspaceId}/data-provider/quickbooks`,
						{ method: "GET" },
					),
				queryKey: integrationQueryKeys.authUrl.quickbooks,
				staleTime: 0,
			});

			if (!result.authUrl) {
				form.setError("data.type", {
					message: "Failed to redirect for QuickBooks authentication",
				});
				return { success: false };
			}

			// Async navigation helper - allows any cleanup before redirect
			await new Promise<void>((resolve) => {
				setLocalStorageItem(
					INTEGRATIONS_LOCAL_STORAGE_KEYS.QUICKBOOKS,
					window.location.pathname,
				);

				// Use setTimeout to allow current execution to complete
				setTimeout(() => {
					window.location.href = result.authUrl;
					// Note: resolve() will never execute due to navigation,
					// but this makes the pattern async-compatible
					resolve();
				}, 0);
			});

			return { success: true };
		} catch (_error) {
			return { success: false };
		}
	},
	steps: [
		{
			description: "Connect QuickBooks to your workspace",
			id: 1,
			title: "QuickBooks",
		},
	],
	type: ProviderType.QUICKBOOKS,
};

export const zoomConfig: ProviderConfig = {
	getSubmitButtonText(_stepIndex, _totalSteps, isSubmitting) {
		if (isSubmitting) return "Redirecting...";

		return "Connect Zoom";
	},
	onFinalSubmit: async (_data, { form, workspaceId }) => {
		try {
			if (!workspaceId) {
				form.setError("data.type", {
					message: "An unexpected error occurred, try again.",
				});
				return { success: false };
			}

			const queryClient = getQueryClient();
			const result = await queryClient.fetchQuery({
				gcTime: 0,
				queryFn: () =>
					queryFetcher<{ authUrl: string }>(
						`/api/workspaces/${workspaceId}/data-provider/zoom`,
						{ method: "GET" },
					),
				queryKey: integrationQueryKeys.authUrl.zoom,
				staleTime: 0,
			});

			if (!result.authUrl) {
				form.setError("data.type", {
					message: "Failed to redirect for Zoom authentication",
				});
				return { success: false };
			}

			await new Promise<void>((resolve) => {
				setLocalStorageItem(
					INTEGRATIONS_LOCAL_STORAGE_KEYS.ZOOM,
					window.location.pathname,
				);

				setTimeout(() => {
					window.location.href = result.authUrl;
					resolve();
				}, 0);
			});

			return { success: true };
		} catch (_error) {
			return { success: false };
		}
	},
	steps: [
		{
			description: "Connect Zoom to your workspace",
			id: 1,
			title: "Zoom",
		},
	],
	type: ProviderType.ZOOM,
};

export const rentengineConfig: ProviderConfig = {
	getSubmitButtonText: (_stepIndex, _totalSteps, isSubmitting) => {
		return isSubmitting ? "Verifying..." : "Verify & Save";
	},
	onFinalSubmit: async (data, { createProvider, pingProvider, form }) => {
		try {
			const pingResult = await pingProvider(data);
			const accountId =
				typeof pingResult?.accountId === "string"
					? pingResult.accountId.trim()
					: typeof pingResult?.providerAccountId === "string"
						? pingResult.providerAccountId.trim()
						: "";

			if (!pingResult?.isCredentialsValid || accountId.length === 0) {
				form.setError("data.credentials.apiKey", {
					message:
						"This provided credential is possibly incorrect or does not have an accessible RentEngine account.",
				});
				return { success: false };
			}

			await createProvider({
				...data,
				data: {
					...data.data,
					credentials: {
						...data.data.credentials,
						accountId,
					},
				},
			} as CreateDataProviderSchema);

			return { success: true };
		} catch (_error) {
			form.setError("data.credentials.apiKey", {
				message:
					"An error occurred while validating the RentEngine API key. Please try again.",
			});
			return { success: false };
		}
	},
	steps: [
		{
			description: `Connect ${PROVIDER_NAMES[ProviderType.RENTENGINE]}`,
			id: 1,
			title: "Connection Credentials",
		},
	],
	type: ProviderType.RENTENGINE,
};

export const podiumConfig: ProviderConfig = {
	getSubmitButtonText: (_stepIndex, _totalSteps, isSubmitting) => {
		return isSubmitting ? "Connecting..." : "Connect Podium";
	},
	onFinalSubmit: async (_data, { form, workspaceId }) => {
		try {
			if (!workspaceId) {
				form.setError("data.type", {
					message: "An unexpected error occurred, try again.",
				});
				return { success: false };
			}

			const queryClient = getQueryClient();
			const result = await queryClient.fetchQuery({
				gcTime: 0,
				queryFn: () =>
					queryFetcher<{ authUrl: string }>(
						`/api/workspaces/${workspaceId}/data-provider/podium`,
						{ method: "GET" },
					),
				queryKey: integrationQueryKeys.authUrl.podium(workspaceId),
				staleTime: 0,
			});

			if (!result.authUrl) {
				form.setError("data.type", {
					message: "Failed to redirect for Podium authentication",
				});
				return { success: false };
			}

			await new Promise<void>((resolve) => {
				setLocalStorageItem(
					INTEGRATIONS_LOCAL_STORAGE_KEYS.PODIUM,
					window.location.pathname,
				);

				setTimeout(() => {
					window.location.assign(result.authUrl);
					resolve();
				}, 0);
			});

			return { success: true };
		} catch (_error) {
			return { success: false };
		}
	},
	steps: [
		{
			description: "Connect Podium to your workspace",
			id: 1,
			title: "Podium",
		},
	],
	type: ProviderType.PODIUM,
};

export const gotoConnectConfig: ProviderConfig = {
	getSubmitButtonText: (_stepIndex, _totalSteps, isSubmitting) => {
		return isSubmitting ? "Connecting..." : "Connect GoTo Connect";
	},
	onFinalSubmit: async (_data, { form, workspaceId }) => {
		try {
			if (!workspaceId) {
				form.setError("data.type", {
					message: "An unexpected error occurred, try again.",
				});
				return { success: false };
			}

			const queryClient = getQueryClient();
			const result = await queryClient.fetchQuery({
				gcTime: 0,
				queryFn: () =>
					queryFetcher<{ authUrl: string }>(
						`/api/workspaces/${workspaceId}/data-provider/goto`,
						{ method: "GET" },
					),
				queryKey: integrationQueryKeys.authUrl.gotoConnect(workspaceId),
				staleTime: 0,
			});

			if (!result.authUrl) {
				form.setError("data.type", {
					message: "Failed to redirect for GoTo Connect authentication",
				});
				return { success: false };
			}

			await new Promise<void>((resolve) => {
				setLocalStorageItem(
					INTEGRATIONS_LOCAL_STORAGE_KEYS.GOTO_CONNECT,
					window.location.pathname,
				);

				setTimeout(() => {
					window.location.assign(result.authUrl);
					resolve();
				}, 0);
			});

			return { success: true };
		} catch (_error) {
			return { success: false };
		}
	},
	steps: [
		{
			description: "Connect GoTo Connect to your workspace",
			id: 1,
			title: "GoTo Connect",
		},
	],
	type: ProviderType.GOTO_CONNECT,
};
// Call Links (VoIP) - Single-step provider that verifies via MD5-hashed database name
export const callLinksConfig: ProviderConfig = {
	getSubmitButtonText: (_stepIndex, _totalSteps, isSubmitting) => {
		return isSubmitting ? "Verifying..." : "Save";
	},

	onFinalSubmit: async (data, { createProvider, pingProvider, form }) => {
		try {
			const pingResult = await pingProvider(data);

			if (!pingResult?.isCredentialsValid) {
				form.setError("data.credentials.databaseName", {
					message:
						"This customer does not currently have a Call Links VoIP account.",
				});
				return { success: false };
			}

			await createProvider(data);
			return { success: true };
		} catch (_error) {
			form.setError("data.credentials.databaseName", {
				message:
					"An error occurred while verifying the account. Please try again.",
			});
			return { success: false };
		}
	},

	steps: [
		{
			description: `Connect ${PROVIDER_NAMES[ProviderType.CALL_LINKS]}`,
			id: 1,
			title: "Connection Credentials",
		},
	],
	type: ProviderType.CALL_LINKS,
};

export const rentvineConfig: ProviderConfig = {
	getSubmitButtonText: (stepIndex, totalSteps, isSubmitting) => {
		if (isSubmitting) return "Saving...";
		return stepIndex < totalSteps - 1 ? "Next" : "Save";
	},
	onFinalSubmit: async (data, { createProvider, form }) => {
		try {
			const selectedContactIds = (form.getValues(
				"data.credentials.generalWorkOrderVendorContactIds" as any,
			) ?? []) as string[];

			const payload = {
				...data,
				data: {
					...data.data,
					credentials: {
						...data.data.credentials,
						generalWorkOrderVendorContactIds: selectedContactIds,
					},
				},
				generalWorkOrderVendorContactIds: selectedContactIds,
			} as CreateDataProviderSchema;

			await createProvider(payload);
			return { success: true };
		} catch (error) {
			const message =
				error instanceof Error && error.message.trim().length > 0
					? error.message
					: "Unable to set up Rentvine data provider";
			form.setError(
				"data.credentials.generalWorkOrderVendorContactIds" as any,
				{
					message,
				},
			);
			return { success: false };
		}
	},
	onInit: (form) => {
		form.setValue("data.credentials.clientId", "placeholder");
		form.setValue("data.credentials.clientSecret", "placeholder");
		form.setValue(
			"data.credentials.generalWorkOrderVendorContactIds" as any,
			[],
		);
	},
	onStepSubmit: async (stepIndex, data, { form, pingProvider }) => {
		if (stepIndex !== 0) {
			if (stepIndex !== 1) {
				return { shouldAdvance: true, success: true };
			}

			const errorFields = [
				"data.credentials.databaseName",
				"data.credentials.clientId",
				"data.credentials.clientSecret",
			] as const;
			const pingResult = await pingProvider(data);
			if (!pingResult?.isCredentialsValid) {
				for (const errorField of errorFields) {
					form.setError(errorField, {
						message: "This provided credential is possibly incorrect",
					});
				}
				return {
					errorFields: [...errorFields],
					shouldAdvance: false,
					success: false,
				};
			}

			const vendors = Array.isArray(pingResult?.vendors)
				? (pingResult.vendors as Array<Record<string, unknown>>)
				: [];
			const vendorOptions = toRentvineVendorOptions(vendors);

			form.setValue(
				"data.credentials.generalWorkOrderVendorContactIds" as any,
				[],
			);
			return {
				data: { vendorOptions },
				shouldAdvance: true,
				success: true,
			};
		}

		const databaseName = form.watch("data.credentials.databaseName") ?? "";
		if (databaseName.trim().length === 0) {
			form.setError("data.credentials.databaseName", {
				message: "Database name is required",
			});
			return { shouldAdvance: false, success: false };
		}

		form.setValue("data.credentials.clientId", "");
		form.setValue("data.credentials.clientSecret", "");

		return { shouldAdvance: true, success: true };
	},
	steps: [
		{
			description: "Provide Rentvine database name",
			id: 1,
			title: "Database Name",
		},
		{
			description: `Connect ${PROVIDER_NAMES[ProviderType.RENTVINE]}`,
			id: 2,
			title: "Connection Credentials",
		},
		{
			description: "Optionally select General Work Order vendors for routing",
			id: 3,
			title: "Work Order Routing",
		},
	],
	type: ProviderType.RENTVINE,
};

export const showMojoConfig: ProviderConfig = {
	getSubmitButtonText: (_stepIndex, _totalSteps, isSubmitting) => {
		return isSubmitting ? "Verifying..." : "Save";
	},

	onFinalSubmit: async (data, { createProvider, pingProvider, form }) => {
		try {
			const pingResult = await pingProvider(data);
			if (!pingResult?.isCredentialsValid) {
				form.setError("data.credentials.apiToken", {
					message:
						"The ShowMojo API token could not be validated. Please confirm the token and try again.",
				});
				return { success: false };
			}

			await createProvider(data);
			return { success: true };
		} catch (_error) {
			form.setError("data.credentials.apiToken", {
				message:
					"An error occurred while validating the ShowMojo API token. Please try again.",
			});
			return { success: false };
		}
	},

	steps: [
		{
			description: `Connect ${PROVIDER_NAMES[ProviderType.SHOWMOJO]}`,
			id: 1,
			title: "Connection Credentials",
		},
	],
	type: ProviderType.SHOWMOJO,
};

export const asanaConfig: ProviderConfig = {
	getSubmitButtonText: (stepIndex, totalSteps, isSubmitting) => {
		if (isSubmitting) return "Saving...";
		return stepIndex < totalSteps - 1 ? "Next" : "Save";
	},
	onFinalSubmit: async (data, { createProvider, form }) => {
		const selectedWorkspaceId =
			(form.watch("data.credentials.workspaceGid" as any) as
				| string
				| undefined) ?? "";
		const workspaceOptions = (form.getValues("data.workspaceOptions" as any) ??
			[]) as {
			label: string;
			value: string;
		}[];
		const selectedProjectIds =
			(form.watch("data.projectIds" as any) as string[] | undefined) ?? [];
		const projectOptions = (form.getValues("data.projectOptions" as any) ??
			[]) as {
			label: string;
			value: string;
		}[];

		if (!selectedWorkspaceId || selectedWorkspaceId === "__placeholder__") {
			form.setError("data.credentials.workspaceGid" as any, {
				message: "Please select a workspace",
			});
			return { success: false };
		}
		if (!Array.isArray(selectedProjectIds) || selectedProjectIds.length === 0) {
			form.setError("data.projectIds" as any, {
				message: "Please select at least one project",
			});
			return { success: false };
		}

		const workspaceName =
			workspaceOptions.find(
				(workspace) => workspace.value === selectedWorkspaceId,
			)?.label ?? selectedWorkspaceId;
		const nameById = new Map(
			projectOptions.map((option) => [option.value, option.label]),
		);
		const projectNames = selectedProjectIds.map(
			(projectId) => nameById.get(projectId) ?? projectId,
		);

		const payload: any = {
			...data,
			data: {
				...data.data,
				credentials: {
					...data.data.credentials,
					workspaceGid: selectedWorkspaceId,
					workspaceName,
				},
				projectIds: selectedProjectIds,
				projectNames,
			},
		};

		await createProvider(payload);
		return { success: true };
	},
	onInit: (form) => {
		form.setValue("data.credentials.workspaceGid" as any, "__placeholder__");
		form.setValue("data.credentials.workspaceName" as any, "__placeholder__");
		form.setValue("data.projectIds" as any, ["__placeholder__"]);
		form.setValue("data.projectNames" as any, ["__placeholder__"]);
	},
	onStepSubmit: async (stepIndex, data, { form, pingProvider }) => {
		if (stepIndex === 0) {
			const token =
				form.watch("data.credentials.personalAccessToken")?.trim() ?? "";
			if (token.length === 0) {
				form.setError("data.credentials.personalAccessToken", {
					message: "Personal access token is required",
				});
				return { shouldAdvance: false, success: false };
			}

			const pingResult = await pingProvider(data);
			if (!pingResult?.isCredentialsValid) {
				form.setError("data.credentials.personalAccessToken", {
					message: "Unable to validate this token with Asana",
				});
				return { shouldAdvance: false, success: false };
			}

			const workspaceOptions = ((pingResult.workspaces as any[]) ?? [])
				.map((workspace) => ({
					label: String(workspace.name ?? ""),
					value: String(workspace.id ?? ""),
				}))
				.filter(
					(workspace) =>
						workspace.label.length > 0 && workspace.value.length > 0,
				)
				.sort((workspaceA, workspaceB) =>
					workspaceA.label.localeCompare(workspaceB.label),
				);

			if (workspaceOptions.length === 0) {
				form.setError("data.credentials.personalAccessToken", {
					message: "No accessible workspaces found for this token",
				});
				return { shouldAdvance: false, success: false };
			}

			form.setValue("data.workspaceOptions" as any, workspaceOptions);
			return {
				data: { workspaceOptions },
				shouldAdvance: true,
				success: true,
			};
		}

		if (stepIndex === 1) {
			const selectedWorkspaceId =
				(form.watch("data.credentials.workspaceGid" as any) as
					| string
					| undefined) ?? "";
			if (!selectedWorkspaceId || selectedWorkspaceId === "__placeholder__") {
				form.setError("data.credentials.workspaceGid" as any, {
					message: "Please select a workspace",
				});
				return { shouldAdvance: false, success: false };
			}

			const workspaceOptions = (form.getValues(
				"data.workspaceOptions" as any,
			) ?? []) as {
				label: string;
				value: string;
			}[];
			const selectedWorkspace = workspaceOptions.find(
				(workspace) => workspace.value === selectedWorkspaceId,
			);

			const pingPayload: any = {
				...data,
				data: {
					...data.data,
					credentials: {
						...data.data.credentials,
						workspaceGid: selectedWorkspaceId,
						workspaceName: selectedWorkspace?.label ?? selectedWorkspaceId,
					},
				},
			};
			const pingResult = await pingProvider(pingPayload);
			if (!pingResult?.isCredentialsValid) {
				form.setError("data.credentials.workspaceGid" as any, {
					message: "Unable to load projects for this workspace",
				});
				return { shouldAdvance: false, success: false };
			}

			const projectOptions = ((pingResult.projects as any[]) ?? [])
				.map((project) => ({
					label: String(project.name ?? ""),
					value: String(project.id ?? ""),
				}))
				.filter(
					(project) => project.label.length > 0 && project.value.length > 0,
				)
				.sort((projectA, projectB) =>
					projectA.label.localeCompare(projectB.label),
				);

			if (projectOptions.length === 0) {
				form.setError("data.credentials.workspaceGid" as any, {
					message: "No accessible projects found for this workspace",
				});
				return { shouldAdvance: false, success: false };
			}

			form.setValue(
				"data.credentials.workspaceName" as any,
				selectedWorkspace?.label,
			);
			form.setValue("data.projectIds" as any, []);
			form.setValue("data.projectNames" as any, []);
			form.setValue("data.projectOptions" as any, projectOptions);

			return {
				data: { projectOptions },
				shouldAdvance: true,
				success: true,
			};
		}

		return { shouldAdvance: true, success: true };
	},
	steps: [
		{
			description: `Connect ${PROVIDER_NAMES[ProviderType.ASANA]}`,
			id: 1,
			title: "Connection Credentials",
		},
		{
			description: "Select one workspace to sync",
			id: 2,
			title: "Workspace",
		},
		{
			description: "Select projects to sync",
			id: 3,
			title: "Projects",
		},
	],
	type: ProviderType.ASANA,
};

// Provider configuration registry
export const PROVIDER_CONFIGS: Record<ProviderType, ProviderConfig> = {
	[ProviderType.YARDI]: yardiConfig,
	[ProviderType.APP_FOOLIO]: appFolioReportingApiConfig,
	[ProviderType.APPFOLIO_DATABASE]: appFolioDatabaseApiConfig,
	[ProviderType.QUICKBOOKS]: quickbooksConfig,
	[ProviderType.ZOOM]: zoomConfig,
	[ProviderType.OPINIION]: opiniionConfig,
	[ProviderType.GADMIN]: gadminConfig,
	[ProviderType.GMELIUS]: gmeliusConfig,
	[ProviderType.CALL_LINKS]: callLinksConfig,
	[ProviderType.PODIUM]: podiumConfig,
	[ProviderType.GOTO_CONNECT]: gotoConnectConfig,
	[ProviderType.RENTENGINE]: rentengineConfig,
	[ProviderType.SHOWMOJO]: showMojoConfig,
	[ProviderType.ASANA]: asanaConfig,
	[ProviderType.RENTVINE]: rentvineConfig,
};
